// ============================================================================
// client/emmure-papier.ts — LE MOINE EMMURÉ, ET SES VARIANTES
// ----------------------------------------------------------------------------
// Quand le joueur rebouche un trou où un moine est tombé, celui-ci n'est pas tué : il reste VIVANT
// dans la brique (`Monk.emmure`), inerte, et ne ressort que si l'on RECREUSE la brique. Il faut
// donc un dessin qui dise trois choses depuis l'autre bout du plateau :
//
//   1. il y a quelque chose de vivant dans cette brique-là          (sinon on l'oublie)
//   2. ce quelque chose est inoffensif tant qu'il y est             (sinon on la contourne)
//   3. la brique peut être rouverte                                 (sinon on ne recreuse jamais)
//
// Ce fichier tient les CANDIDATS, et le jeu n'en appelle qu'un (client/render.ts, drawMonk). Ils
// vivent ici plutôt que dans render.ts pour que la planche /pages/emmure.html appelle EXACTEMENT le
// code du jeu : une maquette approuvée puis redessinée « à peu près » est la façon la plus sûre de
// livrer autre chose que ce qui a été validé.
//
// ⚠️ TOUT EST PROPORTIONNEL À `S`. En jeu S vaut 48, mais la planche a besoin d'agrandir. Les
// constantes en dur de la première version (8.5, 7, 3.2...) valaient pour 48 seulement.
//
// ⚠️ AUCUN ÉTAT, AUCUN `Math.random()`. Ces fonctions sont purement cosmétiques : elles ne lisent
// que le tick de SIMULATION, donc les trois écrans d'une partie en ligne montrent la même chose.
// La variation d'un moine à l'autre vient de `graine` (l'index de sa case), jamais d'un tirage.
// ============================================================================

import { bruit, rgba } from './papier'

type Ctx = CanvasRenderingContext2D

/** Signature commune à toutes les variantes : (contexte, coin haut-gauche de la case, tick, taille de case, graine). */
export type DessinEmmure = (ctx: Ctx, px: number, py: number, t: number, S?: number, graine?: number) => void

/** Le blanc du papier, l'encre, et le creux d'ombre — repris de la DA « papier découpé ». */
const OEIL = '#f6efe0', PUPILLE = '#241b14', TRAIT = '#f2e4cc'

/** Le clignement, décalé de 40 ticks. ⚠️ Sans ce décalage il apparaît les yeux FERMÉS au moment
 *  précis où on l'emmure — le seul instant où le joueur le regarde vraiment. Toute variante qui a
 *  une phase cachée ou fermée doit le reprendre, pour la même raison. */
const cligne = (t: number): boolean => ((t + 40) % 150) < 6

/** Une paire d'yeux dans un creux sombre. Le cœur commun à trois des quatre variantes : c'est le
 *  regard qui dit « vivant », le reste de chaque design dit « emmuré ». */
function yeux(ctx: Ctx, cx: number, cy: number, u: number, t: number, ecart: number, r = 7): void {
  const regard = Math.sin(t * 0.045) * 2.4 * u              // le balayage, lent : il cherche une issue
  for (const dx of [-ecart, ecart]) {
    const x = cx + dx
    ctx.fillStyle = rgba('#140e0a', 0.5)                    // le creux sombre autour de l'oeil
    ctx.beginPath(); ctx.ellipse(x, cy, 8.5 * u * (r / 7), 8.5 * u * (r / 7), 0, 0, Math.PI * 2); ctx.fill()
    if (cligne(t)) {                                        // paupière fermée : un simple trait
      ctx.strokeStyle = TRAIT; ctx.lineWidth = 2.5 * u; ctx.lineCap = 'round'
      ctx.beginPath(); ctx.moveTo(x - 6 * u, cy); ctx.lineTo(x + 6 * u, cy); ctx.stroke()
      continue
    }
    ctx.fillStyle = OEIL                                    // le blanc de l'oeil, GROS
    ctx.beginPath(); ctx.ellipse(x, cy, r * u, (r + 0.5) * u, 0, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = PUPILLE                                 // la pupille
    ctx.beginPath(); ctx.arc(x + regard, cy + 0.5 * u, 3.2 * u * (r / 7), 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = 'rgba(255,255,255,0.85)'                // le petit éclat, sinon le regard est mort
    ctx.beginPath(); ctx.arc(x + regard - 1.4 * u, cy - 1.6 * u, 1.2 * u * (r / 7), 0, Math.PI * 2); ctx.fill()
  }
}

/** Le contour de la capuche du moine, en repère (cx, base) — la silhouette qui le rend
 *  reconnaissable même réduit à une empreinte. Utilisée par « Le fossile » et « Le bombement ». */
function silhouetteCapuche(ctx: Ctx, cx: number, base: number, larg: number, haut: number): void {
  const d = larg / 2
  ctx.beginPath()
  ctx.moveTo(cx - d, base)                                   // épaule gauche
  ctx.quadraticCurveTo(cx - d * 0.96, base - haut * 0.52, cx - d * 0.42, base - haut * 0.84)
  ctx.quadraticCurveTo(cx, base - haut * 1.08, cx + d * 0.42, base - haut * 0.84)  // la pointe de la capuche
  ctx.quadraticCurveTo(cx + d * 0.96, base - haut * 0.52, cx + d, base)            // épaule droite
  ctx.closePath()
}

/* ══════════════════════════ A — LES DEUX YEUX (en place) ══════════════════════════ */

/** Deux grands yeux blancs dans la brique, dont les pupilles balaient lentement, avec un clignement
 *  de temps en temps. Rien d'autre : ni corps, ni capuche, ni ombre — c'est le VIDE autour des yeux
 *  qui raconte qu'il est muré. (31/07, demande Claude-Alain : « on ne voit plus que des gros yeux ».) */
export const dessinYeux: DessinEmmure = (ctx, px, py, t, S = 48) => {
  const u = S / 48
  ctx.save()
  yeux(ctx, px + S / 2, py + S * 0.5, u, t, S * 0.27)
  ctx.restore()
}

/* ══════════════════════════ B — LE FOSSILE ══════════════════════════ */

/** Le moine est devenu une EMPREINTE : sa silhouette encapuchonnée est imprimée en creux dans la
 *  brique, comme un fossile dans la pierre, avec le liseré clair du papier arraché tout autour.
 *  Il ne bouge quasiment pas — seul le regard vit, et la silhouette respire d'un souffle très lent.
 *  C'est la variante la plus calme : elle dit « il fait partie du mur, maintenant ».
 *
 *  ⚠️ Le pari de ce dessin est la SILHOUETTE, pas le détail : à 48 px la capuche pointue se lit
 *  d'un coup d'oeil là où des traits fins deviendraient une tache. */
export const dessinFossile: DessinEmmure = (ctx, px, py, t, S = 48) => {
  const u = S / 48, cx = px + S / 2
  const souffle = Math.sin(t * 0.028) * 0.6 * u              // très lent : le fossile n'est pas mort
  const base = py + S * 0.94 + souffle
  ctx.save()
  // 1. le creux : la silhouette évidée, plus sombre que la brique
  silhouetteCapuche(ctx, cx, base, S * 0.74, S * 0.72 - souffle)
  ctx.fillStyle = rgba('#140e0a', 0.62); ctx.fill()
  // 2. le liseré du papier arraché : un trait clair en haut à gauche, l'ombre portée en bas à droite
  ctx.strokeStyle = rgba('#f2e4cc', 0.5); ctx.lineWidth = 1.6 * u; ctx.stroke()
  ctx.save(); ctx.clip()
  ctx.fillStyle = rgba('#000000', 0.3); ctx.fillRect(px, base - S * 0.3, S, S * 0.4)   // le fond du creux
  ctx.restore()
  // 3. le grain de pierre : quelques éclats fixes, tirés du bruit déterministe de la DA
  ctx.fillStyle = rgba('#f2e4cc', 0.16)
  for (let k = 0; k < 4; k++) {
    const bx = px + 6 * u + bruit(k, 3) * (S - 12 * u), by = py + 8 * u + bruit(3, k) * (S - 16 * u)
    ctx.fillRect(bx, by, 2 * u, 2 * u)
  }
  // 4. le regard, enfoncé dans l'empreinte
  yeux(ctx, cx, base - S * 0.5, u, t, S * 0.19, 5.6)
  ctx.restore()
}

/* ══════════════════════════ C — IL GRATTE ══════════════════════════ */

/** La brique s'est FENDUE, et il travaille : quatre doigts passent par la fente basse et grattent
 *  en vague, un oeil guette par l'entaille du dessus, et la poussière tombe au rythme. C'est la
 *  variante la plus bavarde : elle ne dit pas seulement « il est vivant », elle dit « il essaie de
 *  sortir » — donc elle invite le joueur à recreuser, ou à le laisser mijoter.
 *
 *  ⚠️ C'est l'image de Kilroy : DES DOIGTS QUI DÉPASSENT D'UN BORD. La première version dessinait
 *  une main entière posée sur la fente — à 48 px elle se lisait comme un pansement, et les fissures
 *  en rayons partant du centre comme des brindilles plantées dans le mur. Des demi-doigts alignés
 *  au-dessus d'une fente noire, ça se lit du premier coup ; une main de 12 px, non. */
export const dessinGratte: DessinEmmure = (ctx, px, py, t, S = 48, graine = 0) => {
  const u = S / 48, cx = px + S / 2
  const cote = bruit(graine, 7) > 0.5 ? 1 : -1               // de quel côté il guette : fixe par moine
  const fy = py + S * 0.66                                   // la fente basse, celle des doigts
  const oy = py + S * 0.3                                    // l'entaille haute, celle de l'oeil
  ctx.save()
  ctx.lineCap = 'round'; ctx.lineJoin = 'round'
  // 1. les fissures : des ZIGZAGS qui descendent des entailles vers les bords, comme une vraie
  //    maçonnerie qui lâche. Un trait droit partant du centre ne se lit pas comme une fissure.
  ctx.strokeStyle = rgba('#140e0a', 0.5); ctx.lineWidth = 1.4 * u
  for (const s of [-1, 1]) {
    ctx.beginPath()
    ctx.moveTo(cx + s * S * 0.3, fy)
    ctx.lineTo(cx + s * S * 0.38, fy - S * 0.14)
    ctx.lineTo(cx + s * S * 0.46, fy - S * 0.1)
    ctx.lineTo(cx + s * S * 0.5, fy - S * 0.24)
    ctx.stroke()
  }
  ctx.beginPath()                                            // et une qui file vers le bas
  ctx.moveTo(cx + S * 0.1, fy + S * 0.06); ctx.lineTo(cx + S * 0.04, fy + S * 0.16)
  ctx.lineTo(cx + S * 0.14, fy + S * 0.24); ctx.stroke()
  // 2. LA MAIN. Le dos de la main d'abord, puis les doigts RECOURBÉS qui agrippent le bord.
  //
  //    ⚠️ TOUT ICI SERT À NE PAS FABRIQUER UN VISAGE. Quatre doigts blancs, droits, régulièrement
  //    espacés et centrés au-dessus d'une barre noire, avec un oeil centré dessus, se lisent
  //    comme une bouche pleine de dents surmontée d'un cyclope — c'est ce que faisait la version
  //    d'avant. Trois choses le cassent : la main est DÉCALÉE d'un côté et l'oeil de l'autre (plus
  //    de symétrie, donc plus de visage) ; les doigts sont INÉGAUX en largeur et en écart ; et ils
  //    se REPLIENT (une arche penchée, pas un rectangle plat, qui est la forme d'une dent).
  const mainX = cx - cote * S * 0.1
  const doigts = [{ d: -1.55, w: 2.5, p: -0.16 }, { d: -0.5, w: 2.9, p: -0.06 },
                  { d: 0.55, w: 2.75, p: 0.05 }, { d: 1.5, w: 2.3, p: 0.15 }]
  ctx.fillStyle = rgba('#e6d6ba', 0.95)                      // le dos de la main, qui émerge
  ctx.beginPath(); ctx.roundRect(mainX - 11 * u, fy - 4 * u, 22 * u, 7 * u, 3 * u); ctx.fill()
  doigts.forEach((f, k) => {
    const onde = Math.sin(t * 0.19 - k * 0.8)
    const h = (6.2 + onde * 4) * u                           // amplitude franche : la vague se voit
    const x = mainX + f.d * 6.6 * u
    ctx.save()
    ctx.translate(x, fy); ctx.rotate(f.p)                    // chaque doigt penche autrement
    ctx.fillStyle = TRAIT
    ctx.beginPath(); ctx.roundRect(-f.w * u, -h, f.w * 2 * u, h + 4 * u, [f.w * u, f.w * u, 1 * u, 1 * u]); ctx.fill()
    ctx.fillStyle = rgba('#2e2214', 0.3)                     // le pli de la phalange, EN HAUT du doigt
    ctx.beginPath(); ctx.roundRect(-f.w * 0.62 * u, -h + 1.4 * u, f.w * 1.24 * u, 2.2 * u, 1.1 * u); ctx.fill()
    ctx.restore()
  })
  // 3. la fente basse : elle passe PAR-DESSUS le pied de la main
  ctx.fillStyle = rgba('#140e0a', 0.85)
  ctx.beginPath(); ctx.roundRect(cx - S * 0.36, fy, S * 0.72, 4.6 * u, 2.3 * u); ctx.fill()
  // 4. l'entaille haute, et l'oeil qui guette dedans — DE L'AUTRE CÔTÉ que la main
  ctx.fillStyle = rgba('#140e0a', 0.8)
  ctx.beginPath(); ctx.ellipse(cx + cote * S * 0.17, oy, S * 0.2, 5.2 * u, 0, 0, Math.PI * 2); ctx.fill()
  if (!cligne(t)) {
    const ox = cx + cote * S * 0.19
    ctx.fillStyle = OEIL
    ctx.beginPath(); ctx.ellipse(ox, oy, 6 * u, 4 * u, 0, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = PUPILLE                                  // il regarde ses doigts, puis dehors
    ctx.beginPath(); ctx.arc(ox + Math.sin(t * 0.05) * 1.8 * u, oy + 0.4 * u, 2.6 * u, 0, Math.PI * 2); ctx.fill()
    ctx.fillStyle = 'rgba(255,255,255,0.8)'
    ctx.beginPath(); ctx.arc(ox - 1.2 * u, oy - 1.4 * u, 1 * u, 0, Math.PI * 2); ctx.fill()
  }
  // 5. la poussière qui tombe de la fente : trois grains, décalés en phase
  for (let k = 0; k < 3; k++) {
    const p = ((t * 0.05 + k * 0.33) % 1)
    ctx.globalAlpha = (1 - p) * 0.6
    ctx.fillStyle = '#e0c9a4'
    ctx.fillRect(cx + (k - 1) * S * 0.19, fy + 5 * u + p * S * 0.26, 1.8 * u, 1.8 * u)
  }
  ctx.restore()
}

/* ══════════════════════════ D — LE BOMBEMENT ══════════════════════════ */

/** Il pousse le mur DE L'INTÉRIEUR. La brique se déforme comme une bâche tendue : la capuche
 *  apparaît en RELIEF (bosse claire, ombre portée dessous), qui respire lentement puis donne un
 *  coup d'épaule. Les yeux se devinent dans le creux du relief.
 *
 *  ⚠️ C'est la variante la plus lisible de loin : c'est une FORME qui bouge, pas un détail. Elle
 *  est aussi la plus « cartoon » des trois, ce qui est un choix, pas un accident. */
export const dessinBombement: DessinEmmure = (ctx, px, py, t, S = 48) => {
  const u = S / 48, cx = px + S / 2
  // Respiration lente, plus un COUP D'ÉPAULE toutes les 3 s : sans l'à-coup, la bosse se lit comme
  // une décoration du décor et le joueur oublie qu'il y a quelqu'un dedans.
  const respire = (Math.sin(t * 0.05) + 1) / 2
  const phase = (t % 90) / 90
  const coup = phase < 0.13 ? Math.sin(phase / 0.13 * Math.PI) : 0
  const pousse = respire * 0.16 + coup * 0.34                // 0 -> 0,5 : l'ampleur du relief
  const base = py + S * 0.97
  const larg = S * (0.6 + pousse * 0.2), haut = S * (0.5 + pousse * 0.46)
  ctx.save()
  // 1. l'ombre portée sous la bosse : c'est elle qui crée le volume, pas la couleur
  ctx.save(); ctx.translate(2.2 * u, 3 * u)
  silhouetteCapuche(ctx, cx, base, larg, haut)
  ctx.fillStyle = rgba('#000000', 0.34); ctx.fill()
  ctx.restore()
  // 2. la bosse : la brique tendue, éclaircie parce qu'elle avance vers la lumière.
  // ⚠️ FRANCHEMENT claire. À 0,2 d'opacité elle virait au brun moyen et se confondait avec le
  // creux du « fossile » : deux variantes censées être opposées (l'une rentre, l'autre sort)
  // rendaient la même tache. C'est le contraste qui porte la différence, pas la forme.
  silhouetteCapuche(ctx, cx, base, larg, haut)
  ctx.fillStyle = rgba('#f2e4cc', 0.5 + pousse * 0.42); ctx.fill()
  ctx.strokeStyle = rgba('#2e2214', 0.42); ctx.lineWidth = 1.4 * u; ctx.stroke()
  // 3. les plis de tension au pied de la bosse, comme un tissu tiré
  ctx.strokeStyle = rgba('#2e2214', 0.26); ctx.lineWidth = 1 * u
  for (const s of [-1, 1]) {
    ctx.beginPath()
    ctx.moveTo(cx + s * larg * 0.5, base)
    ctx.quadraticCurveTo(cx + s * larg * 0.66, base - haut * 0.2, cx + s * larg * 0.58, base - haut * 0.42)
    ctx.stroke()
  }
  // 4. le regard, deviné SOUS la surface : plus la bosse avance, plus il devient net
  ctx.globalAlpha = 0.45 + pousse * 1.1
  yeux(ctx, cx, base - haut * 0.56, u, t, larg * 0.24, 5.2)
  ctx.restore()
}

/** LES CANDIDATS, dans l'ordre de la planche. `n` sert de valeur stockée côté page : ne pas
 *  renuméroter, un retour collé dans le fil doit rester lisible dans six mois. */
export const VARIANTES_EMMURE: { n: number; id: string; nom: string; desc: string; dessin: DessinEmmure; enPlace?: boolean }[] = [
  { n: 1, id: 'fossile', nom: 'Le fossile', enPlace: true, dessin: dessinFossile,
    desc: "Choisi par Claude-Alain le 31/07, en jeu depuis. Sa silhouette encapuchonnée imprimée en creux dans la pierre, comme une empreinte. Le plus calme, le plus « il fait partie du mur »." },
  { n: 0, id: 'yeux', nom: 'Les deux yeux', dessin: dessinYeux,
    desc: "La version d'origine, remplacée. Deux grands yeux dans le noir, qui balaient et clignent. Le vide autour raconte le mur." },
  { n: 2, id: 'gratte', nom: 'Il gratte', dessin: dessinGratte,
    desc: "La brique se fend, une main passe et tire, un oeil guette, la poussière tombe. Le seul qui dise « il essaie de sortir »." },
  { n: 3, id: 'bombement', nom: 'Le bombement', dessin: dessinBombement,
    desc: "Il pousse le mur de l'intérieur : la brique se tend en relief, respire, puis donne un coup d'épaule. Le plus lisible de loin." },
]
